import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useSubmitDownloadJob } from './hooks/use-submit-download-job';
import { useDownloadJobs } from './hooks/use-download-jobs';
import { JobHistoryTable } from './job-history-table';
import { segmentedToggleSx } from './toggle-styles';
import { ByPatientPanel } from './by-patient-panel';
import { ByCohortPanel } from './by-cohort-panel';
import { JobRequestDetails } from './mock-data';

/** What the history row needs in order to exist before the server has answered. */
interface PendingJob {
  documentCount: number;
  patientCount: number;
  sizeBytes?: number;
  requestDetails: JobRequestDetails;
}

/**
 * Document Bundle Download.
 *
 * Two modes on one page. The steps are the same; only the order of the first three
 * differs, because By patient lets the resident generate the options beneath them while
 * By cohort derives the roster from the filters (A1, A2, A3).
 *
 * Each panel owns its own filters, roster and summary. This page used to hold a second
 * set for the old cohort form, and when SQ-173 replaced that form the state stayed behind
 * — still calling useDocumentSummary and usePatientList on its own untouched dates. Two
 * callers, one Redux slice: the page's default 30-day reply overwrote the panel's, so the
 * cohort screen reported 114 documents for a window holding 2,965 and the job delivered
 * the larger number.
 *
 * What this page still owns is only what both modes share: the submit call, the job
 * history, and the optimistic row that links them.
 */
const DocumentBundleDownload: React.FC = () => {
  const {
    submit,
    loading: submitting,
    result: submitResult,
    reset: resetSubmit,
  } = useSubmitDownloadJob();
  const { jobs, loading: jobsLoading, cancelJob, addJob } = useDownloadJobs();

  const [mode, setMode] = React.useState<'patient' | 'cohort'>('patient');

  //  Both modes describe the pending row the same way, so one effect adds it.
  const pendingSubmitCtxRef = React.useRef<PendingJob | null>(null);

  //  The row appears the moment the server returns a job id, as QUEUED. That matters for
  //  more than reassurance: useDownloadJobs polls only while an active job is in the list,
  //  so without this the request stays invisible AND unpolled until a manual refresh.
  React.useEffect(() => {
    if (submitResult && pendingSubmitCtxRef.current) {
      const ctx = pendingSubmitCtxRef.current;
      addJob({
        job_id: submitResult.job_id,
        job_name: submitResult.job_name,
        status: 'QUEUED',
        progress_pct: 0,
        created_at: new Date().toISOString(),
        document_count: ctx.documentCount,
        patient_count: ctx.patientCount,
        size_bytes: ctx.sizeBytes,
        request_details: ctx.requestDetails,
      });
      pendingSubmitCtxRef.current = null;
      resetSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitResult]);

  const handlePanelSubmit = (payload: Record<string, unknown>, pending: PendingJob) => {
    if (submitting) return;
    pendingSubmitCtxRef.current = pending;
    submit(payload as never);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography
        sx={{ marginBottom: 3 }}
        fontSize={30}
        color="#111927"
        fontWeight={600}
      >
        Document Bundle Download
      </Typography>

      <Card sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_e, next) => next && setMode(next)}
            sx={{ mb: 3, ...segmentedToggleSx }}
          >
            <ToggleButton value="patient">One patient</ToggleButton>
            <ToggleButton value="cohort">Many patients</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'patient' && (
            <ByPatientPanel
              submitting={submitting}
              onSubmit={handlePanelSubmit}
            />
          )}

          {mode === 'cohort' && (
            <ByCohortPanel
              submitting={submitting}
              onSubmit={handlePanelSubmit}
            />
          )}

          <Divider sx={{ my: 3 }} />

          {/* PP-10 SQ-89: Document Requests */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 1.5 }}
          >
            Document Requests
          </Typography>
          <JobHistoryTable
            jobs={jobs}
            loading={jobsLoading}
            onCancel={cancelJob}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentBundleDownload;
